import React, { useState, useMemo } from 'react';
import { ThemeContext } from './contexts/ThemeContext';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, GlobalStyles } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { getTheme } from './styles/theme';
import LayoutView from './views/LayoutView';
import SiteDashboardView from './views/SiteDashboardView';
import DeployRedirect from './views/SiteDashboardView/DeployRedirect';
import { RoiWorkView } from './views/RoiWorkView';
import { ParkingTestView } from './views/ParkingTestView';
import RealtimeParkingViewWrapper from './components/RealtimeParkingViewWrapper';
import LearningDataView from './views/LearningDataView';
import ProjectFileRepositoryPage from './pages/ProjectFileRepositoryPage';
import ProjectDashboardPage from './pages/ProjectDashboardPage';
import SiteSettingsPage from './pages/SiteSettingsPage';
import SiteVisitLogPage from './pages/SiteVisitLogPage';
import SiteIncidentPage from './pages/SiteIncidentPage';
import SitePhotosPage from './pages/SitePhotosPage';
import SiteMaterialsPage from './pages/SiteMaterialsPage';
import SiteDocumentsPage from './pages/SiteDocumentsPage';
import SiteQaPage from './pages/SiteQaPage';
import QaTemplatePage from './pages/QaTemplatePage';
import SiteSchedulesPage from './pages/SiteSchedulesPage';
import DeploymentResultsPage from './pages/DeploymentResultsPage';
import DeploymentDetailPage from './pages/DeploymentDetailPage';
import OperationProcessPage from './pages/OperationProcessPage';
import OperationProcessTemplatePage from './pages/OperationProcessTemplatePage';
import SimulatorPage from './pages/SimulatorPage';
import LoginPage from './pages/LoginPage';
import AuditLogPage from './pages/AuditLogPage';
import MemoPage from './pages/MemoPage';
import ConsumablesPage from './pages/ConsumablesPage';
import ProtectedRoute from './components/ProtectedRoute';
import RoleProtected from './components/auth/RoleProtected';
import { isAuthenticated } from './utils/auth';

// Map Editor imports
import EditorPage from './map-editor-pages/editor/workers/EditorPage';
import ViewerPage from './map-editor-pages/viewer/ViewerPage';
import DashboardPage from './map-editor-pages/dashboard/DashboardPage';
import GuidedSetupPage from './map-editor-pages/guided/GuidedSetupPage';
import { ThemeProvider as MapEditorThemeProvider } from './map-editor-shared/context/ThemeContext';
import { MapEditorErrorBoundary } from './components/MapEditorErrorBoundary';

// Re-export ThemeContext for backward compatibility (legacy imports from './App')
export { ThemeContext } from './contexts/ThemeContext';

// QueryClient for React Query (used by map-editor)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

function App() {
  const [mode, setMode] = useState<'dark' | 'light'>(() => {
    const saved = localStorage.getItem('themeMode');
    return (saved === 'light' || saved === 'dark') ? saved : 'dark';
  });

  const theme = useMemo(() => getTheme(mode), [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => {
      const newMode = prevMode === 'dark' ? 'light' : 'dark';
      localStorage.setItem('themeMode', newMode);
      return newMode;
    });
  };

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeContext.Provider value={{ mode, toggleTheme }}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {/*
           * map-editor 전역 CSS(global.css)가 `html, body { background: var(--color-bg) }`
           * 를 박는데, 메인 앱은 map-editor 의 data-theme="dark" 를 안 켜서 --color-bg 가
           * 라이트 기본값(#fff)으로 풀린다. 그러면 html 이 흰색이 되어 CssBaseline 의
           * 다크 body 배경이 캔버스로 전파되지 못하고, 문서 흐름 페이지(현장 설정 등)
           * 스크롤 하단에 흰 배경이 샌다. MUI 테마 배경으로 html/body/#root 를 덮어
           * 라이트/다크 모두에서 일관되게 칠한다. (!important 로 global.css 보다 우선)
           */}
          <GlobalStyles
            styles={{
              'html, body, #root': {
                backgroundColor: `${theme.palette.background.default} !important`,
              },
            }}
          />
          <Router>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />

              {/* Redirect root based on auth status */}
              <Route path="/" element={
                isAuthenticated() ? <Navigate to="/projects" replace /> : <Navigate to="/login" replace />
              } />

              {/* Protected Routes */}
              {/* Unified Site Dashboard (현장·배포 통합 관제) - No Layout */}
              <Route path="/projects" element={
                <ProtectedRoute>
                  <SiteDashboardView />
                </ProtectedRoute>
              } />

              {/* Legacy /deploy redirect → /projects (쿼리 보존) */}
              <Route path="/deploy" element={<DeployRedirect />} />

              {/* 공통 메모 — 모든 현장의 메모를 한 화면에서. 날짜가 없는 메모는
                  어떤 기간 조회에도 걸리지 않아 캘린더에서 볼 수 없으므로
                  전용 화면이 필요하다. */}
              <Route path="/memos" element={
                <ProtectedRoute>
                  <MemoPage />
                </ProtectedRoute>
              } />

              {/* 전체 소모품 — 회사 전체 + 현장별 소모품 메모. 누구나 조회 가능 */}
              <Route path="/consumables" element={
                <ProtectedRoute>
                  <ConsumablesPage />
                </ProtectedRoute>
              } />

              {/* 변경 이력 — 누구나 조회 가능 */}
              <Route path="/audit-logs" element={
                <ProtectedRoute>
                  <AuditLogPage />
                </ProtectedRoute>
              } />

              {/* Project Dashboard - No Layout */}
              <Route path="/project/:projectId" element={
                <ProtectedRoute>
                  <ProjectDashboardPage />
                </ProtectedRoute>
              } />

              {/* 현장 설정 - 기본 정보 + 네트워크/IP 관리 */}
              <Route path="/project/:projectId/settings" element={
                <ProtectedRoute>
                  <SiteSettingsPage />
                </ProtectedRoute>
              } />

              {/* 출장 일지 */}
              <Route path="/project/:projectId/journal" element={
                <ProtectedRoute>
                  <SiteVisitLogPage />
                </ProtectedRoute>
              } />

              {/* 장애 이력 */}
              <Route path="/project/:projectId/incidents" element={
                <ProtectedRoute>
                  <SiteIncidentPage />
                </ProtectedRoute>
              } />

              {/* 이미지 보관함 (현장 사진: 랙/외관/케이블/장애 현장) */}
              <Route path="/project/:projectId/photos" element={
                <ProtectedRoute>
                  <SitePhotosPage />
                </ProtectedRoute>
              } />

              {/* 자재 관리 (현장 설정에서 분리한 독립 페이지) */}
              <Route path="/project/:projectId/materials" element={
                <ProtectedRoute>
                  <SiteMaterialsPage />
                </ProtectedRoute>
              } />

              {/* 문서 보관함 (현장별 임의 형식 문서 업로드/다운로드) */}
              <Route path="/project/:projectId/documents" element={
                <ProtectedRoute>
                  <SiteDocumentsPage />
                </ProtectedRoute>
              } />

              {/* 현장 QA (공통 QA 리스트를 가져와 항목별 결과 기록) */}
              <Route path="/project/:projectId/qa" element={
                <ProtectedRoute>
                  <SiteQaPage />
                </ProtectedRoute>
              } />

              {/* 공통 QA 리스트 — 조회는 누구나, 수정은 admin (페이지 안에서 가림) */}
              <Route path="/qa-template" element={
                <ProtectedRoute>
                  <QaTemplatePage />
                </ProtectedRoute>
              } />

              {/* 현장 스케줄 캘린더 */}
              <Route path="/project/:projectId/schedules" element={
                <ProtectedRoute>
                  <SiteSchedulesPage />
                </ProtectedRoute>
              } />

              {/* Operation Process - 현장별 (No Layout, full screen) */}
              <Route path="/project/:projectId/operation-process" element={
                <ProtectedRoute>
                  <OperationProcessPage />
                </ProtectedRoute>
              } />

              {/* Operation Process Template - 글로벌 템플릿 (admin only) */}
              <Route path="/operation-template" element={
                <ProtectedRoute requireRole="admin">
                  <OperationProcessTemplatePage />
                </ProtectedRoute>
              } />

              {/* ROI Editor - editor+ (편집 작업) */}
              <Route path="/project/:projectId/roi-editor" element={
                <ProtectedRoute requireRole="editor">
                  <RoiWorkView
                    projectId={window.location.pathname.split('/')[2]}
                    onBack={() => window.history.back()}
                  />
                </ProtectedRoute>
              } />

              {/* Map Editor Routes - editor+ (편집 작업) */}
              <Route path="/project/:projectId/map-editor" element={
                <ProtectedRoute requireRole="editor">
                  <MapEditorThemeProvider>
                    <DashboardPage />
                  </MapEditorThemeProvider>
                </ProtectedRoute>
              } />
              {/* 가이드 맵 프로젝트 생성 — 페이지 전환형 셋업 위저드 */}
              <Route path="/project/:projectId/map-editor/guided" element={
                <ProtectedRoute requireRole="editor">
                  <MapEditorThemeProvider>
                    <GuidedSetupPage />
                  </MapEditorThemeProvider>
                </ProtectedRoute>
              } />
              <Route path="/project/:projectId/map-editor/:mapId" element={
                <ProtectedRoute requireRole="editor">
                  <MapEditorThemeProvider>
                    {/* 에디터는 iframe 안 라우트로 렌더돼 상위 바운더리가 못 잡는다.
                        여기서 잡지 못한 렌더 예외를 잡아 하얀 화면 대신 오류 카드를 보인다. */}
                    <MapEditorErrorBoundary>
                      <EditorPage />
                    </MapEditorErrorBoundary>
                  </MapEditorThemeProvider>
                </ProtectedRoute>
              } />
              {/* Map Viewer — 조회 전용, viewer 도 접근 가능 */}
              <Route path="/project/:projectId/map-viewer/:mapId" element={
                <ProtectedRoute>
                  <MapEditorThemeProvider>
                    <ViewerPage />
                  </MapEditorThemeProvider>
                </ProtectedRoute>
              } />

              {/* Simulator - editor+ (운영 데이터 발생) */}
              <Route path="/project/:projectId/simulator" element={
                <ProtectedRoute requireRole="editor">
                  <SimulatorPage />
                </ProtectedRoute>
              } />

              {/* Project-based routes with Layout */}
              <Route path="/project/:projectId/*" element={
                <ProtectedRoute>
                  <LayoutView>
                    <Routes>

                      {/* Project Management Pages — role 가드는 inner 단위로 한 번 더 */}

                      {/* 학습 데이터 관리 — editor+ (라벨링/데이터 편집) */}
                      <Route path="learning-data-management" element={
                        <RoleProtected require="editor">
                          <LearningDataView
                            project={{
                              id: window.location.pathname.split('/')[2],
                              name: 'Current Project',
                              description: 'Current working project',
                              location: 'Seoul'
                            }}
                            onBack={() => window.history.back()}
                          />
                        </RoleProtected>
                      } />
                      {/* 주차면 검증 — editor+ (검증 결과 저장) */}
                      <Route path="parking-validation" element={
                        <RoleProtected require="editor">
                          <ParkingTestView
                            project={{
                              id: window.location.pathname.split('/')[2],
                              name: 'Current Project',
                              description: 'Current working project',
                              location: 'Seoul'
                            }}
                            onBack={() => window.history.back()}
                          />
                        </RoleProtected>
                      } />
                      {/* 실시간 현황 — viewer 도 접근 가능 (조회 전용) */}
                      <Route path="live-status" element={
                        <RealtimeParkingViewWrapper />
                      } />
                      {/* 파일 보관함 — admin only (filestorage upload/delete 가 admin) */}
                      <Route path="file-repository" element={
                        <RoleProtected require="admin">
                          <ProjectFileRepositoryPage />
                        </RoleProtected>
                      } />
                      {/* 배포 결과 / 상세 — admin only (배포 트리거 = admin 책임) */}
                      <Route path="deployments" element={
                        <RoleProtected require="admin">
                          <DeploymentResultsPage />
                        </RoleProtected>
                      } />
                      <Route path="deployments/:deploymentId" element={
                        <RoleProtected require="admin">
                          <DeploymentDetailPage />
                        </RoleProtected>
                      } />
                    </Routes>
                  </LayoutView>
                </ProtectedRoute>
              } />
            </Routes>
          </Router>
        </ThemeProvider>
      </ThemeContext.Provider>
    </QueryClientProvider>
  );
}

export default App;